import { useState, useRef, useEffect, useCallback } from "react";

const LS_POS = "aethera_card_pos";
const MARGIN = 8;

const load = () => {
  try {
    return JSON.parse(localStorage.getItem(LS_POS) || "null");
  } catch {
    return null;
  }
};

export const useDraggableCard = (ref, deps = []) => {
  const [pos, setPos] = useState(load);
  const [dragging, setDragging] = useState(false);
  const [desktop, setDesktop] = useState(() => window.innerWidth >= 768);
  const drag = useRef(null);

  const clamp = useCallback(
    (x, y) => {
      const r = ref.current?.getBoundingClientRect();
      const w = r?.width || 300, h = r?.height || 200;
      return {
        x: Math.round(Math.min(Math.max(MARGIN, x), Math.max(MARGIN, window.innerWidth - w - MARGIN))),
        y: Math.round(Math.min(Math.max(MARGIN, y), Math.max(MARGIN, window.innerHeight - h - MARGIN))),
      };
    },
    [ref]
  );

  useEffect(() => {
    const onResize = () => {
      setDesktop(window.innerWidth >= 768);
      setPos((p) => (p ? clamp(p.x, p.y) : p));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [clamp]);

  // re-clamp when card content/size changes (mode toggle, data load)
  useEffect(() => {
    if (!pos) return;
    const id = requestAnimationFrame(() => setPos((p) => (p ? clamp(p.x, p.y) : p)));
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clamp, ...deps]);

  const onPointerDown = (e) => {
    if (!desktop || e.button !== 0) return;
    if (e.target.closest("button, input, a, [data-no-drag]")) return;
    const r = ref.current.getBoundingClientRect();
    drag.current = { dx: e.clientX - r.left, dy: e.clientY - r.top };
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    setPos(clamp(e.clientX - drag.current.dx, e.clientY - drag.current.dy));
  };
  const onPointerUp = (e) => {
    if (!drag.current) return;
    drag.current = null;
    setDragging(false);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    setPos((p) => {
      if (p) localStorage.setItem(LS_POS, JSON.stringify(p));
      return p;
    });
  };

  const reset = () => {
    setPos(null);
    localStorage.removeItem(LS_POS);
  };

  const style = desktop && pos ? { left: pos.x, top: pos.y, right: "auto", bottom: "auto" } : {};

  return { style, dragging, desktop, moved: !!pos, reset, handleProps: { onPointerDown, onPointerMove, onPointerUp } };
};
